/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import { useMemo } from "react";
import { Trans, useTranslation } from "@coworker/locales";
import styled, { css } from "styled-components";
import { RankItemProps } from "./RankItemProps";
import { DecoratedItem } from "./DecoratedItem";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import { useTestBuyFilters } from "../../hooks/useTestBuyFilters";
import {
  DEFUALT_PERIOD_VALUE,
  PERIOD_ID,
} from "../../../MFAQ/hooks/usePeriodFilter";
import { PeriodFilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";

const Container = styled.div`
  padding-top: 16px;
  border-top: 1px solid var(--grey200);
`;

const GrayText = styled.div`
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: var(--grey718);
`;

const Rankings = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 95%;
  margin: 10px auto 0px auto;
  height: 180px;
`;

const Rank = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding-bottom: 2.5rem;
  width: 60px;
  ${(p: { value: number | undefined }) =>
    !p.value &&
    css`
      opacity: 0;
    `}
`;

const RankNumber = styled.div`
  font-weight: bold;
  font-size: 28px;
  display: flex;
  align-items: flex-end;
`;

const Dot = styled.div`
  font-size: 26px;
`;

const Description = styled.div`
  font-size: 12px;
  width: 90px;
  text-align: center;
`;

function formatHFB(hfb: number | undefined) {
  return `HFB ${hfb}`;
}

function RankItem({ hfb, value, rank, decorated }: RankItemProps) {
  return (
    <>
      {decorated ? (
        <DecoratedItem rank={rank} hfb={hfb} value={value} />
      ) : (
        <Rank value={value}>
          <RankNumber>
            {rank}
            <Dot>.</Dot>
          </RankNumber>
          <Description>{formatHFB(hfb) + " - " + value}</Description>
        </Rank>
      )}
    </>
  );
}

interface RankingShowcaseProps {
  ahead: RankItemProps;
  active: RankItemProps;
  behind: RankItemProps;
  isWholeStoreActive: boolean;
  offsetIntoCounts: number;
  totalTestbuysDone: number;
  loading: boolean;
}

export function RankingShowcase({
  ahead,
  active,
  behind,
  isWholeStoreActive,
  offsetIntoCounts,
  totalTestbuysDone,
  loading,
}: RankingShowcaseProps) {
  const { t } = useTranslation();
  const testbuyFilters = useTestBuyFilters();

  const period = getSelectedFilterValue(
    PERIOD_ID,
    testbuyFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ) as PeriodFilterValue;

  const periodTitle = useMemo(() => {
    if (period.value === "period=7days") {
      return t("last7DaysString");
    } else if (period.value === "period=4weeks") {
      return t("last4WeeksString");
    } else {
      return `${period.startDate} - ${period.endDate}`;
    }
  }, [period]);

  const isRankingAvailable =
    !!totalTestbuysDone && !isWholeStoreActive && offsetIntoCounts !== -1;

  if (loading || !isRankingAvailable) return null;

  return (
    <Container>
      <GrayText>
        <Trans>testBuysDoneString</Trans>
      </GrayText>
      <GrayText>{periodTitle}</GrayText>
      <Rankings>
        <RankItem {...ahead} />
        <RankItem decorated {...active} />
        <RankItem {...behind} />
      </Rankings>
    </Container>
  );
}
